import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {Header1} from 'ui-library/Headers';

const DEFAULT_BLOCK_HEIGHT = 400;

export interface BlockSpec {
  key: string;
  block: JSX.Element;
  minHeight?: number;
  maxHeight?: number;
  spansFullWidth?: boolean;
}

interface DashboardSection {
  header?: string;
  blockSpecs: Array<BlockSpec>;
}

interface DashboardLayoutProps {
  expandedBlockKey?: string | null;
  sections: Array<DashboardSection>;
}

/**
 * Render the given blocks with a dashboard layout of up to 2 blocks per row
 *
 * This dashboard layout is inspired by Insights dashboards.
 */
export default function DashboardLayout(props: DashboardLayoutProps): JSX.Element {
  const expandedBlockSpec = props.sections
    .flatMap((section) => section.blockSpecs)
    .find((blockSpec) => blockSpec.key === props.expandedBlockKey);

  if (expandedBlockSpec != null) {
    return <div className={css(styles.expandedBlock)}>{expandedBlockSpec.block}</div>;
  } else {
    return (
      <div className={css(styles.dashboard)}>
        {props.sections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {section.header != null && <Header1>{section.header}</Header1>}
            <div className={css(styles.dashboardSectionBlocks)}>
              {section.blockSpecs.map(({block, minHeight, key, maxHeight, spansFullWidth}) => (
                <div
                  key={key}
                  style={{
                    minHeight: minHeight ?? DEFAULT_BLOCK_HEIGHT,
                    maxHeight: maxHeight ?? DEFAULT_BLOCK_HEIGHT,
                    ...(spansFullWidth ? {gridColumn: '1 / 3'} : {}),
                  }}
                >
                  {block}
                </div>
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  }
}

const MIN_BLOCK_WIDTH = 300;
const GAP = ANALYSIS_TOOL_SPACING;
const NUM_COLUMNS = 2;

const styles = StyleSheet.create({
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: GAP,
  },
  dashboardSectionBlocks: {
    display: 'grid',
    gap: GAP,
    // This is essentially saying that we want to fit as many column as possible in our container such that the
    // columns are at least `MIN_BLOCK_WIDTH` wide and that we have at most `NUM_COLUMNS` columns. `repeat()`
    // with `auto-fit` is what defines this "fit as many columns as possible" behavior. (In this context, read
    // this as "repeat the sizing constraint of the second arg of `repeat()` however many times we can make it
    // fit in the row based on the container's size").
    //
    // To get the actual number of columns in each row, we use `minmax()` to define the allowable range of its
    // size. Min is whatever arg is smaller in `minmax()`. The base range is [`MIN_BLOCK_WIDTH`, `1fr`], meaning
    // that we want to repeat adding columns that are 1 fraction of the container's width wide where the number
    // of columns is a number such that 1 fraction of the width > MIN_BLOCK_WIDTH. To restrict the number of
    // columns to be less than or equal to `NUM_COLUMNS`, we use `max()` to add an additional restriction
    // to make it such that 1 fraction < ( size of the container / max columns). Since there's some gap between
    // columns, size of the container needs to be adjusted by the total gap between columns in this case.
    gridTemplateColumns: `
      repeat(
        auto-fit,
        minmax(
          max(
            ${MIN_BLOCK_WIDTH}px,
            calc((100% - ${NUM_COLUMNS}*${GAP}px)/${NUM_COLUMNS})
          ),
          1fr
        )
      )`,
  },

  expandedBlock: {
    height: '80vh',
  },
});
